import { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { subscribeToNewsletter } from '../../utils/firebase/firebase'
import bg from '../../assets/images/cardiff/hyve-gym-cardiff-10.jpeg'
import './styles.scss'

const validationSchema = yup.object({
  firstName: yup
    .string()
    .trim()
    .min(2, 'Please enter your first name')
    .required('First name is required'),
  email: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Email is required'),
})

/**
 * Public newsletter signup landing page (trainhyve.com/newsletter).
 * Enrols visitors into the HYVE Cardiff Resend segment via subscribeToNewsletter.
 */
function Newsletter() {
  const [subscribed, setSubscribed] = useState(false)

  const formik = useFormik({
    initialValues: { firstName: '', email: '' },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await subscribeToNewsletter({
          firstName: values.firstName.trim(),
          email: values.email.trim().toLowerCase(),
        })
        setSubscribed(true)
      } catch (error) {
        toast.error('Something went wrong. Please try again.')
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <section
      className='newsletter'
      style={{ '--newsletter-bg': `url(${bg})` }}
    >
      <div className='newsletter__overlay' />
      <div className='newsletter__noise' />

      <div className='newsletter__inner'>
        {subscribed ? (
          <div className='newsletter__success'>
            <div className='newsletter__successIcon'>
              <CheckCircleOutlineIcon fontSize='inherit' />
            </div>
            <h1 className='newsletter__successTitle'>
              You're <span className='accent'>in.</span>
            </h1>
            <p className='newsletter__successText'>
              Welcome to the HYVE Cardiff newsletter — check your inbox for a
              little hello. See you soon. 💪
            </p>
            <a className='newsletter__backLink' href='https://trainhyvecardiff.com'>
              Explore HYVE Cardiff <ArrowForwardIcon fontSize='inherit' />
            </a>
          </div>
        ) : (
          <div className='newsletter__content'>
            <p className='newsletter__eyebrow'>HYVE CARDIFF · NEWSLETTER</p>

            <h1 className='newsletter__title'>
              Stronger
              <br />
              <span className='accent'>together.</span>
            </h1>

            <p className='newsletter__lede'>
              New classes, member offers, events and training tips — straight to
              your inbox. No noise, just the good stuff.
            </p>

            <ul className='newsletter__pills'>
              <li>Class drops</li>
              <li>Member offers</li>
              <li>Training tips</li>
            </ul>

            <form className='newsletter__form' onSubmit={formik.handleSubmit} noValidate>
              <div className='newsletter__field'>
                <input
                  id='firstName'
                  name='firstName'
                  type='text'
                  placeholder='First name'
                  autoComplete='given-name'
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  aria-label='First name'
                />
                {formik.touched.firstName && formik.errors.firstName && (
                  <span className='newsletter__error'>{formik.errors.firstName}</span>
                )}
              </div>

              <div className='newsletter__field'>
                <input
                  id='email'
                  name='email'
                  type='email'
                  placeholder='Email address'
                  autoComplete='email'
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  aria-label='Email address'
                />
                {formik.touched.email && formik.errors.email && (
                  <span className='newsletter__error'>{formik.errors.email}</span>
                )}
              </div>

              <button
                type='submit'
                className='newsletter__submit'
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? (
                  <span className='newsletter__spinner' aria-label='Submitting' />
                ) : (
                  <>
                    Subscribe <ArrowForwardIcon fontSize='inherit' />
                  </>
                )}
              </button>
            </form>

            <p className='newsletter__fineprint'>
              We'll only email you the essentials. Unsubscribe any time.
            </p>
          </div>
        )}
      </div>
    </section>
  )
}

export default Newsletter
